import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';

interface ClientNotification {
  id: number;
  type: string | null;
  message: string | null;
  is_read: number | null;
  created_at: string | null;
  /** Entité liée (mission, commande…) : sert à ouvrir la bonne page. */
  publication_id: number | null;
}

// Icône + couleurs par type (aligné avec la cloche marchand, en style « dressing »).
const TYPE_META: Record<string, { icon: string; className: string; label: string }> = {
  order: { icon: 'ri-shopping-bag-3-line', className: 'bg-foreground-100 text-foreground-700', label: 'Commande' },
  mission: { icon: 'ri-megaphone-line', className: 'bg-primary-100 text-primary-700', label: 'Mission' },
  candidature: { icon: 'ri-megaphone-line', className: 'bg-primary-100 text-primary-700', label: 'Candidature' },
  payment: { icon: 'ri-bank-card-line', className: 'bg-foreground-100 text-foreground-700', label: 'Paiement' },
  booking: { icon: 'ri-calendar-check-line', className: 'bg-accent-100 text-accent-700', label: 'Rendez-vous' },
  message: { icon: 'ri-chat-3-line', className: 'bg-secondary-100 text-secondary-700', label: 'Message' },
  contact: { icon: 'ri-mail-line', className: 'bg-secondary-100 text-secondary-700', label: 'Contact' },
  store_transfer: { icon: 'ri-exchange-funds-line', className: 'bg-secondary-100 text-secondary-700', label: 'Transfert' },
  loyalty_points: { icon: 'ri-star-line', className: 'bg-accent-100 text-accent-700', label: 'Fidélité' },
  system: { icon: 'ri-information-line', className: 'bg-background-200/70 text-foreground-600', label: 'Info' },
};

function meta(type: string | null) {
  return TYPE_META[type || ''] || { icon: 'ri-notification-3-line', className: 'bg-background-200/70 text-foreground-600', label: 'Notification' };
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '';
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return "à l'instant";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `il y a ${days} j`;
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

/**
 * Cible de navigation au clic sur une notification client.
 * Chaque type mène à SA page réelle : la mission concernée, la commande,
 * la messagerie, le planning, le portefeuille… — plus jamais dans le vide.
 */
function targetFor(type: string | null, publicationId: number | null): string {
  const id = Number(publicationId) || 0;
  switch (type) {
    case 'mission':
    case 'candidature':
      return id > 0 ? `/mon-compte/missions/${id}` : '/mon-compte/missions';
    case 'order':
      return '/mon-compte/orders';
    case 'payment':
    case 'payment_failed':
      return '/mon-compte/payments';
    case 'message':
    case 'contact':
      return '/mon-compte/messages';
    case 'booking':
      return '/mon-planning';
    case 'store_transfer':
    case 'money_request':
    case 'loyalty_points':
      return '/mon-compte/wallet';
    case 'form':
      return '/mon-compte/messages';
    default:
      return '/mon-compte/overview';
  }
}

/**
 * Cloche de notifications de l'espace client. Liste les notifications
 * (table `notifications`) adressées à l'utilisateur connecté, avec le
 * compteur de non-lues, le marquage lu et un rafraîchissement temps réel.
 */
export default function ClientNotificationsBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState<ClientNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const fetchNotifications = useCallback(() => {
    if (!user?.id) return;
    setLoading(true);
    supabase
      .from('notifications')
      .select('id, type, message, is_read, created_at, publication_id')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(30)
      .then(({ data, error }) => {
        if (!error) setNotifications((data || []) as ClientNotification[]);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Temps réel : nouvelle notification pour cet utilisateur.
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel('client-notifications-bell')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications', filter: `user_id=eq.${user.id}` },
        () => fetchNotifications(),
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user, fetchNotifications]);

  // Fermeture au clic extérieur.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const unreadCount = notifications.filter((n) => n.is_read === 0).length;

  const markAsRead = (id: number) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: 1 } : n)));
    supabase.from('notifications').update({ is_read: 1 }).eq('id', id).then(() => {}).catch(() => {});
  };

  const handleClick = (n: ClientNotification) => {
    markAsRead(n.id);
    setOpen(false);
    navigate(targetFor(n.type, n.publication_id));
  };

  const markAllAsRead = () => {
    if (!user?.id) return;
    setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
    supabase
      .from('notifications')
      .update({ is_read: 1 })
      .eq('user_id', user.id)
      .eq('is_read', 0)
      .then(() => {})
      .catch(() => {});
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative w-9 h-9 rounded-lg flex items-center justify-center text-foreground-500 hover:text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
        title="Mes notifications"
      >
        <i className="ri-notification-3-line text-lg"></i>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 px-1 rounded-full bg-accent-500 text-background-50 text-[9px] font-bold flex items-center justify-center leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-background-50 border border-background-200/40 rounded-xl overflow-hidden z-50 animate-scale-in origin-top-right shadow-lg">
          <div className="flex items-center justify-between px-4 h-12 border-b border-background-200/40">
            <span className="text-sm font-semibold text-foreground-900">Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-1 text-xs text-foreground-500 hover:text-foreground-800 font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-check-double-line"></i>
                Tout marquer comme lu
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <i className="ri-loader-4-line animate-spin text-xl text-foreground-400"></i>
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <div className="w-12 h-12 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-notification-off-line text-xl text-foreground-400"></i>
                </div>
                <p className="mt-3 text-sm font-medium text-foreground-700">Aucune notification</p>
                <p className="mt-1 text-xs text-foreground-400">Vous serez informé ici de vos commandes et rendez-vous.</p>
              </div>
            ) : (
              <div className="divide-y divide-background-200/40">
                {notifications.map((n) => {
                  const m = meta(n.type);
                  return (
                    <button
                      key={n.id}
                      onClick={() => handleClick(n)}
                      className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors cursor-pointer ${
                        n.is_read === 0 ? 'bg-accent-50/40 hover:bg-accent-50/70' : 'hover:bg-background-100'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${m.className}`}>
                        <i className={`${m.icon} text-base`}></i>
                      </div>
                      <div className="min-w-0 flex-1">
                        <span className={`inline-flex items-center gap-1 mb-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap ${m.className}`}>
                          <i className={m.icon}></i>{m.label}
                        </span>
                        <p className="text-sm text-foreground-800 leading-snug break-words">{n.message || 'Nouvelle notification'}</p>
                        <p className="mt-1 text-xs text-foreground-400">{timeAgo(n.created_at)}</p>
                      </div>
                      {n.is_read === 0 && <span className="w-2 h-2 rounded-full bg-accent-500 flex-shrink-0 mt-1.5"></span>}
                      <i className="ri-arrow-right-s-line text-foreground-300 flex-shrink-0 mt-1.5"></i>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="border-t border-background-200/40 px-4 py-2 flex justify-center">
            <button
              onClick={fetchNotifications}
              className="flex items-center gap-1.5 text-xs text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer"
            >
              <i className="ri-refresh-line"></i>
              <span>Actualiser</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}